import {
  SettingsPermissions,
  UserPermissions,
} from "@redotech/account-common/permissions";
import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { ReclaimFlagKey } from "@redotech/feature-flags-keys-reclaim/keys";
import { RedoProduct } from "@redotech/redo-common-model/redo-product";
import { IntegrationKind } from "@redotech/redo-integrations-core";
import { PermissionHandle } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { IntegrationStatusKey } from "@redotech/redo-merchant-integrations-rpc/schema/returns-rpc/get-all-integration-statuses";
import type { Gen2IntegrationKind } from "@redotech/redo-return-core-common/integrations/gen2-integration-kinds";
import type { CarrierBrandSlug } from "./carrier-brands";
import {
  IntegrationCatalogEntry,
  IntegrationTypeCategory,
} from "./catalog-types";

const R = SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS;
const S = SettingsPermissions.SET_SUPPORT_SETTINGS;
const M = SettingsPermissions.SET_MARKETING_SETTINGS;
const CA = SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS;
const OF = SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS;
const CO = SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS;
const G = SettingsPermissions.SET_GENERAL_SETTINGS;
const SH = SettingsPermissions.SET_SHIPPING_SETTINGS;

const erpWms3pl: IntegrationCatalogEntry[] = [
  {
    slug: "aims360",
    name: "Aims360",
    description: "Connect your Aims360 account to automatically sync returns",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.AIMS360 },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Aims360"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/aims360",
  },
  {
    slug: "apparel-magic",
    name: "ApparelMagic",
    description: "Connect your ApparelMagic account to Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.APPAREL_MAGIC },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["ApparelMagic", "Oms-ApparelMagic"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/apparel-magic",
  },
  {
    slug: "blue-cherry",
    name: "BlueCherry",
    description: "Connect your BlueCherry WMS account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BLUE_CHERRY },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["BlueCherry"],
    gen3Kinds: [],
  },
  {
    slug: "controlport",
    name: "ControlPort",
    description: "Connect your ControlPort warehouse to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CONTROLPORT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ControlPort"],
    gen3Kinds: [],
  },
  {
    slug: "mintsoft",
    name: "Mintsoft",
    description: "Connect your Mintsoft WMS account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.MINTSOFT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Mintsoft"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/mintsoft",
  },
  {
    slug: "blue-yonder",
    name: "Blue Yonder",
    description: "Connect your Blue Yonder WMS account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BLUEYONDER },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["BlueYonder"],
    gen3Kinds: [],
  },
  {
    slug: "brightpearl",
    name: "Brightpearl",
    description: "Connect your Brightpearl account to Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BRIGHTPEARL },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Brightpearl"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/brightpearl",
  },
  {
    slug: "cin7-omni",
    name: "Cin7 Omni",
    description:
      "Connect your Cin7 Omni account to sync returns and credit notes",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CIN7_OMNI },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Cin7Omni"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "cloudx",
    name: "CloudX/Bergen Logistics",
    description: "Connect your CloudX/Bergen Logistics account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CLOUDX },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CloudX"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/cloudx",
  },
  {
    slug: "deposco",
    name: "Deposco",
    description:
      "Connect your Deposco WMS account to automatically sync return RMAs",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.DEPOSCO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Deposco"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/deposco",
    isBeta: true,
  },
  {
    slug: "ecwid",
    name: "Ecwid",
    description:
      "Create Ecwid orders automatically for returns, claims, or warranties.",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.ECWID },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Ecwid"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/ecwid",
    isBeta: true,
  },
  {
    slug: "flexport",
    name: "Flexport",
    description: "Connect your Flexport account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.FLEXPORT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Flexport"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/flexport",
  },
  {
    slug: "netsuite",
    name: "NetSuite",
    description: "Connect your NetSuite account to Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.NETSUITE },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["NetSuite", "Oms-NetSuite"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/netsuite",
  },
  {
    slug: "shipbob",
    name: "ShipBob",
    description: "Connect your ShipBob account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPBOB },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipBob"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipbob",
  },
  {
    slug: "shipedge",
    name: "ShipEdge",
    description: "Connect your ShipEdge account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPEDGE },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Shipedge"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipedge",
  },
  {
    slug: "shiphero",
    name: "ShipHero",
    description: "Connect your ShipHero account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPHERO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipHero"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shiphero",
  },
  {
    slug: "indigo8",
    name: "Indigo8",
    description: "Connect your Indigo8 account to sync returns automatically.",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.INDIGO8 },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Indigo8"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "odoo",
    name: "Odoo",
    description:
      "Connect your Odoo account to create credit notes for returns automatically.",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.ODOO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Odoo"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "shipmonk",
    name: "ShipMonk",
    description: "Connect your ShipMonk account to sync returns automatically.",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPMONK },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipMonk"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipmonk",
  },
  {
    slug: "masonhub",
    name: "MasonHub",
    description: "Connect your MasonHub account to sync returns automatically.",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.MASONHUB },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Masonhub"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/masonhub",
    isBeta: true,
  },
  {
    slug: "linnworks",
    name: "Linnworks",
    description: "Connect Linnworks directly to Redo for order fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "LinnworksDirect" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["LinnworksDirect"],
    gen3Kinds: [],
  },
  {
    slug: "luminous",
    name: "Luminous",
    description: "Connect your Luminous account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.LUMINOUS },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Luminous"],
    gen3Kinds: [],
  },
  {
    slug: "seko",
    name: "Seko",
    description: "Connect your Seko account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SEKO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Seko"],
    gen3Kinds: [],
  },
  {
    slug: "trackstar",
    name: "TrackStar",
    description: "Connect your TrackStar account to Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.TRACKSTAR },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Trackstar"],
    gen3Kinds: [],
    providerDocs: [
      {
        name: "BlueBox",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/bluebox",
      },
      {
        name: "Cirro",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/cirro",
      },
      {
        name: "Nimble",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/nimble",
      },
      {
        name: "ShipStream",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipstream",
      },
    ],
  },
  {
    slug: "ryder",
    name: "RyderShip",
    description: "Connect your RyderShip account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.RYDER },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Ryder"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/rydership",
  },
  {
    slug: "teamwork-commerce",
    name: "Teamwork Commerce",
    description: "Connect your Teamwork Commerce account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: {
      source: "statusKey",
      key: IntegrationStatusKey.TEAMWORK_COMMERCE,
    },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["TeamworkCommerce"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "cin7-core",
    name: "Cin7 Core",
    description: "Sync orders and fulfillments with Cin7 Core",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "Cin7Core" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Cin7Core"],
    gen3Kinds: [],
  },
  {
    slug: "shipstation",
    name: "ShipStation",
    description: "Sync orders and shipments with ShipStation",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "ShipStationApi" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["ShipStationApi"],
    gen3Kinds: [],
  },
  {
    slug: "dsco",
    name: "Dsco",
    description: "Connect your Dsco account to sync orders with Redo",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen3", kind: IntegrationKind.Dsco },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Dsco },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Dsco],
  },
];

const ecommercePlatforms: IntegrationCatalogEntry[] = [
  {
    slug: "shopify",
    name: "Shopify",
    description: "Your Shopify store powers Redo across every product",
    products: [
      RedoProduct.RETURNS,
      RedoProduct.SUPPORT,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.CUSTOMER_ACCOUNTS,
      RedoProduct.CONVERSION,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "platform", platform: "shopify" },
    action: { behavior: "readOnly" },
    permissions: [G],
    gen2Kinds: ["Shopify"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/shopify",
  },
  {
    slug: "aptos",
    name: "Aptos",
    description: "Sync Aptos retail orders into Redo",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Aptos" },
    action: { behavior: "detailPanel" },
    permissions: [OF],
    gen2Kinds: ["Aptos"],
    gen3Kinds: [],
  },
  {
    slug: "rithum",
    name: "Rithum",
    description: "Sync Rithum (CommerceHub) dropship orders with Redo",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Rithum" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Rithum"],
    gen3Kinds: [],
  },
  {
    slug: "sps-commerce",
    name: "SPS Commerce",
    description: "Sync SPS Commerce purchase orders and shipments with Redo",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "SpsCommerce" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["SpsCommerce"],
    gen3Kinds: [],
    adminOnly: true,
    isBeta: true,
  },
  {
    slug: "amazon",
    name: "Amazon",
    description: "Pull Amazon orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "AmazonSellingPartners" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["AmazonSellingPartners"],
    gen3Kinds: [],
  },
  {
    slug: "walmart",
    name: "Walmart",
    description: "Pull Walmart marketplace orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Walmart" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Walmart"],
    gen3Kinds: [],
  },
  {
    slug: "ebay",
    name: "eBay",
    description: "Pull eBay orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Ebay" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Ebay"],
    gen3Kinds: [],
  },
  {
    slug: "faire",
    name: "Faire",
    description: "Pull Faire wholesale orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Faire" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Faire"],
    gen3Kinds: [],
  },
  {
    slug: "woocommerce",
    name: "WooCommerce",
    description: "Pull WooCommerce orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "WooCommerce" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["WooCommerce"],
    gen3Kinds: [],
  },
  {
    slug: "wayfair",
    name: "Wayfair",
    description: "Pull Wayfair orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Wayfair" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Wayfair"],
    gen3Kinds: [],
  },
  {
    slug: "temu",
    name: "Temu",
    description: "Pull Temu orders into Redo fulfillment",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "UnifiedTemu" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["UnifiedTemu"],
    gen3Kinds: [],
    featureFlag: OmsFlagKey.TEMU_INTEGRATION,
  },
  {
    slug: "tiktok-shop",
    name: "TikTok Shop",
    description: "Connect your TikTok Shop to manage returns through Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    // Not the hub key: that one stays returns-gated for the legacy page.
    status: { source: "gen2Kind", kind: "UnifiedTiktok" },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["OmsTiktok", "UnifiedTiktok"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/tiktok",
    isBeta: true,
  },
  {
    slug: "bigcommerce",
    name: "BigCommerce",
    description: "BigCommerce order sync, set up by the Redo team",
    // One doc: storefront coverage extension, returns refunds/exchanges,
    // Recover cart triggers, order-tracking provider resolution, order sync.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.COVERAGE,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Bigcommerce" },
    action: { behavior: "contactRedo" },
    permissions: [R, OF, M],
    gen2Kinds: ["Bigcommerce"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/bigcommerce",
    adminOnly: true,
  },
  {
    slug: "commerce-cloud",
    name: "Salesforce Commerce Cloud",
    description: "Commerce Cloud order sync, set up by the Redo team",
    // Not OMS (marketplace provider is a stub): returns order sync, coverage
    // cartridge, and marketing segmentation via SFCC loyalty-tier reads.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "CommerceCloud" },
    action: { behavior: "detailPanel" },
    permissions: [R, M],
    gen2Kinds: ["CommerceCloud"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/salesforce-commerce-cloud",
    adminOnly: true,
  },
  {
    slug: "comment-sold",
    name: "Comment Sold",
    description: "Connect your Comment Sold account to Redo",
    // Coverage: order sync ingests Redo coverage charges; denied-claim PP+
    // refunds also run through this integration.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    // Not the hub key: that one stays existence-based for the legacy page.
    status: { source: "gen2Kind", kind: "Commentsold" },
    action: { behavior: "activationOnly" },
    permissions: [R, OF, M],
    gen2Kinds: ["Commentsold"],
    gen3Kinds: [],
  },
];

const returnsServices: IntegrationCatalogEntry[] = [
  {
    slug: "loop",
    name: "Loop",
    description: "Connect your Loop returns account to Redo",
    // Order tracking mints Loop return deeplinks for non-Redo-Returns teams.
    products: [RedoProduct.RETURNS, RedoProduct.ORDER_TRACKING],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.LOOP },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Loop"],
    gen3Kinds: [IntegrationKind.Loop],
    // Deliberately hidden from merchants (competitive sensitivity — Loop is a
    // competitor; Jordan/Will 2026-07-16). Do not remove adminOnly.
    adminOnly: true,
  },
  {
    slug: "narvar",
    name: "Narvar",
    description: "Connect your Narvar returns account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.NARVAR },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Narvar"],
    gen3Kinds: [],
    adminOnly: true,
    isBeta: true,
  },
  {
    slug: "return-logic",
    name: "ReturnLogic",
    description: "Connect your ReturnLogic returns account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.RETURNLOGIC },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ReturnLogic"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "inmar",
    name: "Inmar",
    description: "Connect your Inmar account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.INMAR },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Inmar"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/returns-services/inmar",
  },
  {
    slug: "idf-shipping",
    name: "IDF Shipping",
    description: "Connect your IDF Shipping account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.IDF_SHIPPING },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["IDFShipping"],
    gen3Kinds: [],
  },
  {
    slug: "refundid",
    name: "Refundid",
    description: "Connect your Refundid account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.REFUNDID },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Refundid"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/refundid",
  },
  {
    slug: "purple-dot",
    name: "Purple Dot",
    description: "Connect your Purple Dot account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.PURPLE_DOT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["PurpleDot"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/purple-dot",
  },
  {
    slug: "katana",
    name: "Katana",
    description: "Connect your Katana account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.KATANA },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Katana"],
    gen3Kinds: [],
  },
  {
    slug: "custom-return",
    name: "Custom Return Integration",
    description: "Set up a custom return integration",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.CUSTOM_RETURN },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CustomReturnIntegration"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/custom",
  },
  {
    slug: "extensiv",
    name: "Extensiv",
    description:
      "Connect your Extensiv (3PL Central) account to automatically sync return RMAs",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.EXTENSIV },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Extensiv"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/extensiv",
  },
  {
    slug: "sfom",
    name: "SFOM",
    description: "SFOM returns sync, set up by the Redo team",
    products: [RedoProduct.RETURNS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.SFOM },
    action: { behavior: "contactRedo" },
    permissions: [R],
    gen2Kinds: ["SFOM"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "happy-returns",
    name: "Happy Returns",
    description: "Happy Returns dropoff, set up by the Redo team",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "gen2Kind", kind: "HappyReturns" },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["HappyReturns"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "liquidonate",
    name: "LiquiDonate",
    description:
      "Route eligible returns to a nearby nonprofit through LiquiDonate",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "gen2Kind", kind: "Liquidonate" },
    action: { behavior: "detailPanel" },
    permissions: [R],
    gen2Kinds: ["Liquidonate"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "yofi",
    name: "Yofi",
    description: "Yofi fraud and bot detection, set up by the Redo team",
    // Returns consumes it: return flagging, fraud scores, claim charges.
    products: [RedoProduct.RETURNS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "teamSetting", setting: "yofi" },
    action: { behavior: "contactRedo" },
    permissions: [R],
    gen2Kinds: [],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "falcon",
    name: "Redo Offer Network",
    description:
      "Show personalized offers on your tracking and thank-you pages",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Falcon" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Falcon"],
    gen3Kinds: [],
  },
  {
    slug: "rokt",
    name: "Rokt",
    description: "Show personalized Rokt offers on your tracking page",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Rokt" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Rokt"],
    gen3Kinds: [],
  },
  {
    slug: "uptick",
    name: "Uptick",
    description:
      "Show personalized Uptick offers on your Shopify thank-you and order status pages",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Uptick" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    featureFlag: MarketingFlagKey.UPTICK_INTEGRATION,
    gen2Kinds: ["Uptick"],
    gen3Kinds: [],
  },
];

const smsEmailMarketing: IntegrationCatalogEntry[] = [
  {
    slug: "klaviyo",
    name: "Klaviyo",
    description:
      "Connect your Klaviyo account to automatically sync customer events",
    products: [
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.CUSTOMER_ACCOUNTS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.KLAVIYO },
    action: { behavior: "activationOnly" },
    permissions: [R, M, CA, G],
    gen2Kinds: ["Klaviyo"],
    gen3Kinds: [IntegrationKind.Klaviyo],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/klaviyo",
  },
  {
    slug: "cordial",
    name: "Cordial",
    description:
      "Connect your Cordial account to automatically sync customer events",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.CORDIAL },
    action: {
      behavior: "settingsPage",
      route: "/settings/integrations/cordial",
    },
    permissions: [R],
    gen2Kinds: ["Cordial"],
    gen3Kinds: [],
  },
  {
    slug: "attentive",
    name: "Attentive",
    description: "Connect Attentive for SMS support and marketing",
    // Order tracking: the marketing-purpose doc's ORDER_TRACKING event drives
    // Attentive tracking notifications (tracking-event-handlers).
    products: [
      RedoProduct.RETURNS,
      RedoProduct.SUPPORT,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Attentive" },
    action: { behavior: "activationOnly" },
    permissions: [R, S, M],
    gen2Kinds: ["Attentive"],
    gen3Kinds: [IntegrationKind.Attentive],
  },
  {
    slug: "postscript",
    name: "Postscript",
    description: "Connect Postscript for SMS support and marketing",
    // Order tracking: the same ORDER_TRACKING event registration the marketing
    // row toggles is what the order-tracking sender dispatches on.
    products: [
      RedoProduct.SUPPORT,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Postscript" },
    action: { behavior: "activationOnly" },
    permissions: [S, M],
    gen2Kinds: ["Postscript"],
    gen3Kinds: [IntegrationKind.Postscript],
  },
  {
    slug: "google-ads",
    name: "Google Ads",
    description: "Connect your Redo segments to your Google Ads customer lists",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "GoogleAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["GoogleAds"],
    gen3Kinds: [],
  },
  {
    slug: "meta-ads",
    name: "Meta Ads & Lead Forms",
    description:
      "Sync Redo segments to Meta custom audiences and capture leads from Meta forms",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "MetaAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["MetaAds"],
    gen3Kinds: [],
  },
  {
    slug: "tiktok-ads",
    name: "TikTok Ads",
    description: "Sync Redo segments to TikTok Ads custom audiences",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "TiktokAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["TiktokAds"],
    gen3Kinds: [],
    featureFlag: MarketingFlagKey.TIKTOK_ADS_INTEGRATION,
  },
  {
    slug: "orders-api",
    name: "Orders API",
    description: "Send order events to Redo marketing via the Orders API",
    // The doc flips the team's primary provider to REDO_API: outbound
    // fulfillment enablement (oms) and return side effects ride on it.
    products: [RedoProduct.MARKETING, RedoProduct.OMS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "OrdersApi" },
    action: { behavior: "activationOnly" },
    permissions: [M, OF, R],
    gen2Kinds: ["OrdersApi"],
    gen3Kinds: [],
    docsUrl: "https://developers.redo.com/api-reference/orders/create-order",
  },
  {
    slug: "bubblehouse",
    name: "Bubblehouse",
    description:
      "Award loyalty points via Bubblehouse when a return is processed",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.BUBBLEHOUSE },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Bubblehouse"],
    gen3Kinds: [],
  },
  {
    slug: "creditsyard",
    name: "CreditsYard",
    description: "Connect your CreditsYard account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.CREDITSYARD },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CreditsYard"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/creditsyard",
  },
  {
    slug: "rise-ai",
    name: "Rise.ai",
    description: "Connect your Rise.ai account to Redo",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.RISE_AI },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["RiseAi"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/rise-ai",
  },
  {
    slug: "loopz",
    name: "Loopz",
    description: "Connect your Loopz account to issue gift cards on returns",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.LOOPZ },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Loopz"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "stay-ai",
    name: "Stay AI",
    description: "Sync Stay AI subscriptions with Redo",
    // One API-key doc serves marketing sync, reclaim dispute evidence, returns
    // coverage cleanup, and the tracking page's subscription-upsell block.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.RECLAIM,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "StayAi" },
    action: { behavior: "activationOnly" },
    permissions: [M, G, R],
    gen2Kinds: ["StayAi"],
    gen3Kinds: [],
  },
  {
    slug: "growave",
    name: "Growave",
    description: "Connect Growave to enable marketing automation triggers",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Growave" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Growave"],
    gen3Kinds: [],
    // The legacy marketing card is commented out ("inactive for now"), so
    // merchants never had a Growave surface — staff-managed until it launches.
    adminOnly: true,
  },
  {
    slug: "google-merchant-center",
    name: "Google Merchant Center",
    description: "Google Merchant Center sync, set up by the Redo team",
    // Consumers are the IMS product-listing feed, not marketing.
    products: [RedoProduct.OMS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "GoogleMerchantCenter" },
    action: { behavior: "contactRedo" },
    permissions: [OF],
    gen2Kinds: ["GoogleMerchantCenter"],
    gen3Kinds: [],
    adminOnly: true,
  },
];

const reviewsLoyaltySubscriptions: IntegrationCatalogEntry[] = [
  {
    slug: "judgeme",
    name: "Judge.me",
    description: "Connect Judge.me to sync product reviews with Redo",
    // Reviews feed marketing review sync and the conversion checkout widget.
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Judgeme },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Judgeme },
    permissions: [M, CO],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Judgeme],
  },
  {
    slug: "loyalty-lion",
    name: "LoyaltyLion",
    description: "Connect LoyaltyLion to sync loyalty points with Redo",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.LoyaltyLion },
    action: { behavior: "gen3Connect", kind: IntegrationKind.LoyaltyLion },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.LoyaltyLion],
  },
  {
    slug: "stamped",
    name: "Stamped",
    description: "Connect Stamped to show product reviews in Redo checkout",
    // Reviews are read at checkout render time; there is no marketing sync.
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Stamped },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Stamped },
    permissions: [CO],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Stamped],
  },
  {
    slug: "yotpo",
    name: "Yotpo",
    description: "Connect Yotpo to sync reviews and loyalty with Redo",
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Yotpo },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Yotpo },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Yotpo],
  },
  {
    slug: "okendo",
    name: "Okendo",
    description: "Connect Okendo to sync reviews and loyalty with Redo",
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    // AnyKind so a reviews-only (Okendo) merchant reads connected — otherwise
    // the flag-off visibility gate strands them off the Okendo page entirely.
    status: { source: "gen2AnyKind", kinds: ["Okendo", "OkendoMarketing"] },
    action: { behavior: "activationOnly" },
    permissions: [M, CO],
    gen2Kinds: ["Okendo", "OkendoMarketing"],
    gen3Kinds: [],
    featureFlag: MarketingFlagKey.OKENDO_MARKETING_INTEGRATION,
  },
  {
    slug: "smile",
    name: "Smile.io",
    description: "Connect Smile.io to surface loyalty points in Redo messaging",
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Smile },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Smile },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Smile],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/smile",
  },
  {
    slug: "recharge",
    name: "Recharge",
    description: "Sync Recharge subscriptions with Redo",
    // One credential doc serves marketing upsells, support subscription
    // management, returns-side coverage cleanup, tracking-page upsells, and
    // dispute evidence.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.SUPPORT,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen2Kind", kind: "Recharge" },
    action: { behavior: "activationOnly" },
    permissions: [M, S, R, G],
    // Legacy parity: Recharge connected from the marketing-settings page or the
    // support channels card, so the Returns row admits either.
    productPermissions: { [RedoProduct.RETURNS]: [M, S] },
    gen2Kinds: ["Recharge"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/recharge",
  },
  {
    slug: "skio",
    name: "Skio",
    description: "Sync Skio subscriptions with Redo",
    // One API-key doc serves marketing sync, returns-side coverage cleanup,
    // the tracking page's subscription-upsell block, and dispute evidence.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen2Kind", kind: "Skio" },
    action: { behavior: "activationOnly" },
    permissions: [M, R, G],
    // Legacy parity: the sole Skio connect surface was the marketing-settings
    // page, so the Returns row reuses that gate instead of 403ing on its RPC.
    productPermissions: { [RedoProduct.RETURNS]: [M] },
    gen2Kinds: ["Skio"],
    gen3Kinds: [],
  },
  {
    slug: "loop-subscriptions",
    name: "Loop Subscriptions",
    description: "Use Loop subscription history as Redo dispute evidence",
    // Read-only: the token is read on demand per dispute, with no sync.
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.LoopSubscriptions },
    action: {
      behavior: "gen3Connect",
      kind: IntegrationKind.LoopSubscriptions,
    },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.LoopSubscriptions],
  },
  {
    slug: "rivo",
    name: "Rivo",
    description: "Connect your Rivo account to Redo",
    products: [RedoProduct.CUSTOMER_ACCOUNTS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "statusKey", key: IntegrationStatusKey.RIVO },
    action: { behavior: "activationOnly" },
    permissions: [CA, R],
    gen2Kinds: ["Rivo"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/rivo",
  },
];

const supportChannels: IntegrationCatalogEntry[] = [
  {
    slug: "support-email",
    name: "Email",
    description: "Connect Gmail or Outlook to answer support email in Redo",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: {
      source: "gen2AnyKind",
      kinds: ["Gmail", "Outlook"],
      orSetting: "customSenderEmails",
    },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/supportEmail",
      connectRoute: "/settings/channels/email-connect",
    },
    permissions: [S],
    gen2Kinds: ["Gmail", "Outlook"],
    gen3Kinds: [],
  },
  {
    slug: "facebook-messenger",
    name: "Facebook Messenger",
    description: "Answer Facebook DMs and comments from Redo support",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2AnyKind", kinds: ["Facebook", "Instagram"] },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/meta-settings",
      connectRoute: "/settings/channels/meta-connect",
    },
    permissions: [S],
    gen2Kinds: ["Facebook"],
    gen3Kinds: [],
  },
  {
    slug: "instagram",
    name: "Instagram",
    description: "Answer Instagram DMs and comments from Redo support",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2AnyKind", kinds: ["Facebook", "Instagram"] },
    // Instagram shares Meta's connect + settings surfaces with Facebook.
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/meta-settings",
      connectRoute: "/settings/channels/meta-connect",
    },
    permissions: [S],
    gen2Kinds: ["Instagram"],
    gen3Kinds: [],
  },
  {
    slug: "tiktok-support",
    name: "TikTok Messaging",
    description: "Answer TikTok messages from Redo support",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2Kind", kind: "Tiktok" },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/tiktok-settings",
    },
    permissions: [S],
    gen2Kinds: ["Tiktok"],
    gen3Kinds: [],
  },
  {
    slug: "chat-widget",
    name: "Custom website chat widget",
    description: "Chat with shoppers on your storefront through Redo support",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "teamSetting", setting: "customerWidget" },
    action: { behavior: "settingsPage", route: "/settings/customer-widget" },
    permissions: [S],
    gen2Kinds: [],
    gen3Kinds: [],
  },
  {
    slug: "sms-voice",
    name: "SMS/Voice",
    description: "Support shoppers over text and phone calls in Redo",
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "teamSetting", setting: "supportPhone" },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/voice-settings",
      connectRoute: "/settings/channels/phone-setup",
    },
    permissions: [S],
    gen2Kinds: [],
    gen3Kinds: [],
  },
  {
    slug: "gorgias",
    name: "Gorgias",
    description:
      "Escalate support tickets to Gorgias and pull ticket history for dispute evidence",
    // One shared connection: Support escalation + Reclaim dispute evidence +
    // Recover outreach suppression/handoff (marketing).
    products: [RedoProduct.SUPPORT, RedoProduct.RECLAIM, RedoProduct.MARKETING],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Gorgias" },
    action: { behavior: "activationOnly" },
    permissions: [S, G, M],
    // Legacy parity: the Recover card showed this status to marketing or
    // support, matching getGorgiasConnectionStatus's own gate.
    productPermissions: { [RedoProduct.MARKETING]: [M, S] },
    gen2Kinds: ["Gorgias"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/gorgias",
  },
  {
    slug: "kustomer",
    name: "Kustomer",
    description: "Kustomer helpdesk sync, set up by the Redo team",
    products: [RedoProduct.SUPPORT, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Kustomer" },
    action: { behavior: "contactRedo" },
    permissions: [S],
    gen2Kinds: ["Kustomer"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "richpanel",
    name: "Richpanel",
    description: "Escalate support conversations to Richpanel over SMS",
    // Recover SMS conversations (marketing) escalate to Richpanel and resume
    // on ticket resolution via the same connection.
    products: [RedoProduct.SUPPORT, RedoProduct.MARKETING],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Richpanel" },
    action: { behavior: "activationOnly" },
    permissions: [S, M],
    // Same as Gorgias: the Recover card's status was readable by marketing or
    // support, while connecting always meant the support-gated connect page.
    productPermissions: { [RedoProduct.MARKETING]: [M, S] },
    gen2Kinds: ["Richpanel"],
    gen3Kinds: [],
  },
];

const paymentsFinance: IntegrationCatalogEntry[] = [
  {
    slug: "authorizenet",
    name: "Authorize.net",
    description: "Connect your Authorize.net account to process refunds",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "statusKey", key: IntegrationStatusKey.AUTHORIZENET },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["AuthorizeNet"],
    gen3Kinds: [],
  },
  {
    slug: "stripe",
    name: "Stripe",
    description: "Connect Stripe to manage payment disputes in Redo",
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    // Disputes connect/sync still runs on the Gen II Mongo doc; the Gen III
    // config exists but nothing writes its credentials yet.
    status: { source: "gen2Kind", kind: "Stripe" },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["Stripe"],
    gen3Kinds: [IntegrationKind.Stripe],
  },
  {
    slug: "paypal",
    name: "PayPal",
    description: "Connect PayPal to manage payment disputes in Redo",
    // Reclaim (disputes) surface + return/denied-claim refund consumption +
    // IMS agentic-commerce payment capture (oms).
    products: [
      RedoProduct.RETURNS,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
      RedoProduct.OMS,
    ],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "PayPal" },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["PayPal"],
    gen3Kinds: [],
  },
  {
    slug: "adyen",
    name: "Adyen",
    description: "Adyen payments, set up by the Redo team",
    // Refunds both return charges and denied-coverage-claim PP+ fees.
    products: [RedoProduct.RETURNS, RedoProduct.COVERAGE, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "Adyen" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Adyen"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "optty",
    name: "Optty",
    description: "Optty payments, set up by the Redo team",
    // Refunds both return charges and denied-coverage-claim PP+ fees.
    products: [RedoProduct.RETURNS, RedoProduct.COVERAGE, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "Optty" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Optty"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "disputely",
    name: "Disputely",
    description: "Disputely dispute automation, powering RESOLVE deflections",
    products: [RedoProduct.RECLAIM, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.Disputely },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["Disputely"],
    gen3Kinds: [IntegrationKind.Disputely],
    // Same gate the legacy RESOLVE surface used: the card is hidden unless the
    // flag is on, and stays reachable for teams already enrolled.
    featureFlag: ReclaimFlagKey.DISPUTE_RESOLVE_UI,
  },
  {
    slug: "quickbooks-online",
    name: "QuickBooks Online",
    description: "Connect QuickBooks Online to sync inventory and accounting",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.QuickBooksOnline },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.QuickBooksOnline],
    featureFlag: OmsFlagKey.IMS_QUICKBOOKS,
  },
];

const analytics: IntegrationCatalogEntry[] = [
  {
    slug: "google-analytics",
    name: "Google Analytics",
    description: "Connect Google Analytics to optimize for AI search",
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.GoogleAnalytics },
    action: { behavior: "gen3Connect", kind: IntegrationKind.GoogleAnalytics },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.GoogleAnalytics],
  },
  {
    slug: "google-search-console",
    name: "Google Search Console",
    description: "Connect Google Search Console to optimize for AI search",
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.GoogleSearchConsole },
    action: {
      behavior: "gen3Connect",
      kind: IntegrationKind.GoogleSearchConsole,
    },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.GoogleSearchConsole],
  },
  {
    slug: "triple-whale",
    name: "Triple Whale",
    description: "Send Redo data to Triple Whale analytics",
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.TripleWhale },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.TripleWhale],
  },
];

const crossBorder: IntegrationCatalogEntry[] = [
  {
    slug: "globale",
    name: "Global-e",
    description: "Connect your Global-e account to Redo",
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "statusKey", key: IntegrationStatusKey.GLOBALE },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["GlobalE"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/global-e",
  },
  {
    slug: "landmark-global",
    name: "Landmark Global",
    description: "Cross-border fulfillment through Landmark Global",
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "gen2Kind", kind: "LandmarkGlobal" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["LandmarkGlobal", "RedoLandmarkGlobal"],
    gen3Kinds: [],
  },
  {
    slug: "zonos",
    name: "Zonos",
    description: "Zonos cross-border duties, set up by the Redo team",
    // Landed-cost provider: conversion inclusive pricing + xborder outbound.
    products: [RedoProduct.OMS, RedoProduct.CONVERSION, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "gen3", kind: IntegrationKind.Zonos },
    action: { behavior: "contactRedo" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Zonos],
    adminOnly: true,
  },
];

/** Self-serve carrier brands; status folds all the brand's account variants
 * (EasyPost-brokered and standalone) via connectedCarrierBrands. */
const merchantCarrier = (
  slug: CarrierBrandSlug,
  name: string,
  products: readonly RedoProduct[] = [RedoProduct.OMS, RedoProduct.RETURNS],
  gen2Kinds: readonly Gen2IntegrationKind[] = [],
): IntegrationCatalogEntry => ({
  slug,
  name,
  description: `Connect your ${name} carrier account for shipping labels`,
  products,
  type: IntegrationTypeCategory.CARRIER,
  status: { source: "carrier", brand: slug },
  action: { behavior: "detailPanel" },
  permissions: [SH],
  gen2Kinds,
  gen3Kinds: [],
});

const carriers: IntegrationCatalogEntry[] = [
  merchantCarrier("amazon-shipping", "Amazon Shipping"),
  merchantCarrier("apc", "APC"),
  merchantCarrier("asendia", "Asendia"),
  merchantCarrier("australia-post", "Australia Post"),
  merchantCarrier("canpar-express", "Canpar Express"),
  merchantCarrier("dhl-express", "DHL Express"),
  merchantCarrier(
    "ecom-express",
    "Ecom Express",
    [RedoProduct.OMS, RedoProduct.CONVERSION],
    ["EcomExpressCarrierAccount"],
  ),
  merchantCarrier("epost-global", "ePost Global"),
  merchantCarrier("eta-express", "ETA Express", [
    RedoProduct.OMS,
    RedoProduct.CONVERSION,
  ]),
  merchantCarrier("first-mile", "FirstMile"),
  merchantCarrier("gls", "GLS"),
  merchantCarrier("jitsu", "Jitsu", [RedoProduct.ORDER_TRACKING]),
  merchantCarrier("ontrac", "OnTrac"),
  merchantCarrier("passport-global", "Passport Global", [RedoProduct.OMS]),
  merchantCarrier("purolator", "Purolator"),
  merchantCarrier("royal-mail", "Royal Mail"),
  merchantCarrier("seko-omniparcel", "OmniParcel - Seko"),
  merchantCarrier("spee-dee", "SpeeDee"),
  merchantCarrier("veho", "Veho"),
  {
    slug: "parent-easypost",
    name: "EasyPost",
    description: "Connect your EasyPost account for outbound labels",
    // Order tracking: the doc's webhookSecret authenticates EasyPost tracker
    // webhooks, the source of this team's tracker events.
    products: [
      RedoProduct.OMS,
      RedoProduct.RETURNS,
      RedoProduct.CONVERSION,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "gen2Kind", kind: "ParentEasypost" },
    // Account-level connect lives on the OMS activation row; Returns rides it.
    action: { behavior: "activationOnly" },
    permissions: [OF],
    // Status-only row: EasyPost tracking has no settings surface of its own,
    // so the account's own permission reads it rather than marketing settings.
    productPermissions: { [RedoProduct.ORDER_TRACKING]: [OF] },
    gen2Kinds: ["ParentEasypost"],
    gen3Kinds: [],
  },
  {
    slug: "fedex",
    name: "FedEx",
    description: "Connect your FedEx carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "fedex" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["FedExCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "usps",
    name: "USPS",
    description: "Connect your USPS carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "usps" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UspsCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "uniuni",
    name: "UniUni",
    description: "Connect your UniUni carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "uniuni" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UniUniCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "dhl-ecommerce",
    name: "DHL eCommerce",
    description:
      "Connect your DHL eCommerce carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "dhl-ecommerce" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["DhlEcommerceDirectCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "sendcloud",
    name: "Sendcloud",
    description: "Connect your Sendcloud carrier account for return labels",
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "sendcloud" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["SendcloudCarrierAccount", "SendcloudCarrierContract"],
    gen3Kinds: [],
  },
  {
    slug: "stallion-express",
    name: "Stallion Express",
    description:
      "Connect your Stallion Express carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "stallion-express" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["StallionExpressCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "chitchats",
    name: "ChitChats",
    description: "Connect your ChitChats carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "chitchats" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["ChitChatsCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "canada-post",
    name: "Canada Post",
    description: "Connect your Canada Post carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "canada-post" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: [
      "CanadaPostCarrierAccount",
      "CanadaPostDeveloperPortalCarrierAccount",
    ],
    gen3Kinds: [],
  },
  {
    slug: "ups",
    name: "UPS",
    description: "Connect your UPS carrier account for shipping labels",
    products: [RedoProduct.OMS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "ups" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UpsGhostAccountIntegration"],
    gen3Kinds: [],
  },
  {
    slug: "easypost-carrier",
    name: "EasyPost Carrier Accounts",
    description: "Child EasyPost carrier accounts, set up by the Redo team",
    products: [
      RedoProduct.OMS,
      RedoProduct.RETURNS,
      RedoProduct.CONVERSION,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "gen2Kind", kind: "Easypost" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Easypost"],
    gen3Kinds: [],
    adminOnly: true,
  },
];

export const INTEGRATIONS_CATALOG: readonly IntegrationCatalogEntry[] = [
  ...erpWms3pl,
  ...ecommercePlatforms,
  ...returnsServices,
  ...smsEmailMarketing,
  ...reviewsLoyaltySubscriptions,
  ...supportChannels,
  ...paymentsFinance,
  ...analytics,
  ...crossBorder,
  ...carriers,
];

/** Any-of hub/vendor route gate: the union of every permission that grants at
 * least one catalog card, so anyone who can see a card can reach the page. */
export const INTEGRATIONS_HUB_PERMISSIONS: readonly UserPermissions[] = [
  ...new Set(INTEGRATIONS_CATALOG.flatMap((entry) => entry.permissions)),
];

/** Route-permission source for deep pages: keeps card visibility and page access in lockstep. */
export function catalogRoutePermission(slug: string): PermissionHandle {
  const entry = INTEGRATIONS_CATALOG.find(
    (candidate) => candidate.slug === slug,
  );
  if (!entry) {
    // Runs at module scope while the route tree is built: a throw would
    // white-screen the app, and [] reads as "no gate" — so deny this route.
    console.error(`No integrations catalog entry for slug: ${slug}`);
    return () => false;
  }
  return [...entry.permissions];
}
